import { Injectable } from '@angular/core';
import { CableBurialService } from './cable-burial-service';
import { InternetOutageService } from './internet-outage.service';
import {
  ActionType,
  ApiSendChatMessageResponseActionTypes,
  ApiSendChatMessageResponseMessageTitleTypes,
  ClickableLinkActionType,
  ComponentType,
  MessageSubType,
  MessageType,
  RealmDomain,
} from '../types/enum';
import { getBrand } from '../../utils/getBrand';

@Injectable({
  providedIn: 'root',
})
export class ConverterService {
  constructor(
    private cableBurialService: CableBurialService,
    private internetOutageService: InternetOutageService
  ) {}
  clickableLinkActionMapping: UrlActionType[] = [
    {
      urlPattern: 'vachat://signIn/yes',
      actionType: ClickableLinkActionType.SIGN_IN_YES,
    },
    {
      urlPattern: 'vachat://signIn/no',
      actionType: ClickableLinkActionType.SIGN_IN_NO,
    },
    {
      urlPattern: 'vachat://continueConversation/yes',
      actionType: ClickableLinkActionType.CONTINUE_CONVERSATION,
    },
    {
      urlPattern: 'vachat://networkOutage/getUserCoordinates',
      actionType: ClickableLinkActionType.USE_MY_COORDINATES,
    },
  ];

  specialMessageTitles = ['cable_burial_status#', 'internet_outage#', 'whix#'];

  private isActionTypeClickableLink(actionType: string): boolean {
    return actionType === ApiSendChatMessageResponseActionTypes.CLICKABLE_LINK;
  }
  private isActionTypeQuickReply(actionType: string): boolean {
    return actionType === ApiSendChatMessageResponseActionTypes.QUICK_REPLY;
  }
  private isActionTypeFeedback(actionType: string): boolean {
    return actionType === ApiSendChatMessageResponseActionTypes.THUMBS_UP_DOWN;
  }

  getUpdatedChatMessageResponse = (apiResponse: ApiSendChatMessageResponse) => {
    const duplicateMessageTitles: Set<string> = new Set();
    return apiResponse.response.flatMap((resp) => {
      let includesSpecialTitle = false;
      if (resp.message[0].messageTitle) {
        includesSpecialTitle = this.specialMessageTitles.some((messageTitle) =>
          resp.message[0].messageTitle.includes(messageTitle)
        );
      }
      if (includesSpecialTitle) {
        if (duplicateMessageTitles.has(resp.message[0].messageTitle)) {
          resp.message[0].messageTitle = '';
          return [
            {
              message: resp.message,
            },
          ];
        } else {
          duplicateMessageTitles.add(resp.message[0].messageTitle);
          const messageTitle = resp.message[0].messageTitle;
          resp.message[0].messageTitle = '';
          return [
            {
              message: [
                {
                  messageText: '',
                  messageTitle: messageTitle,
                },
              ],
            },
            {
              message: resp.message,
            },
          ];
        }
      }
      return resp;
    });
  };

  /**
   * Converts the response from the dialog api call to array of Message
   *
   * @param {ApiSendChatMessageResponse} apiResponse: The response from the dialog API call
   * @return {Message[]} array of Message
   */
  convertSendChatMessageResponse = (
    apiResponse: ApiSendChatMessageResponse
  ): Message[] => {
    let result: Message[] = [];
    if (apiResponse.echo) {
      // Save Session Id
    }
    const apiResponseUpdated = this.getUpdatedChatMessageResponse(apiResponse);
    if (apiResponseUpdated) {
      result = apiResponseUpdated.map(
        (response: ApiSendChatMessageResponseResponse, index: number) => {
          return {
            messageType: MessageType.VA,
            time: new Date(),
            isMessageTypeSameAsPrevious: !!index,
            isQuickReply: this.isActionTypeQuickReply(
              response.message[0]?.action?.actionType
            ),
            messageComponent: this.createMessageComponent(
              response.message[0],
              index,
              apiResponse.echo
            ),
            messageSubType:
              response.message[0].messageTitle ===
              ApiSendChatMessageResponseMessageTitleTypes.PAY_ONLINE_THROUGH_CREDITCARD
                ? MessageSubType.PAYMENT
                : undefined,
          };
        }
      );
      this.addEscalationMessage(result, apiResponse.escalation);
    }
    return result;
  };

  /**
   * Creates a Message Component
   *
   * @param {ApiSendChatMessageResponseMessage} message: the message property from the response
   * @param {number} index: index of the message
   * @return {MessageComponent} the message Component
   */

  createMessageComponent(
    message: ApiSendChatMessageResponseMessage,
    index: number,
    echo: any
  ): MessageComponent {
    let messageComponent: MessageComponent = {};
    if (message.action) {
      messageComponent = this.createDialogComponent(message, index);
    }
    const cableBurialMessageData =
      this.cableBurialService.checkIfBurailMessageTitleExist(
        message.messageTitle
      );
    if (cableBurialMessageData) {
      messageComponent = this.cableBurialService.createObjectForBurialComponent(
        cableBurialMessageData,
        echo
      );
    }
    const internetOutageMessageData =
      this.internetOutageService.checkIfOutageMessageTitleExist(
        message.messageTitle
      );
    if (internetOutageMessageData) {
      messageComponent =
        this.internetOutageService.createObjectForOutageComponent(
          internetOutageMessageData
        );
    }
    return messageComponent;
  }

  createDialogComponent(
    message: ApiSendChatMessageResponseMessage,
    index: number
  ): MessageComponent {
    let clickableLinks: ClickableLink[] = [];
    let cannedResponse: CannedResponse[] = [];
    if (this.isActionTypeClickableLink(message.action.actionType)) {
      clickableLinks = this.createClickableLinks(message.action.actionItem);
    } else {
      cannedResponse = this.createCannedResponse(
        message.messageTitle,
        message.action.actionItem
      );
    }
    return {
      component: ComponentType.DialogMessageComponent,
      componentData: {
        isUserMessage: false,
        timeStamp: new Date(),
        cardText: message.messageText,
        isMessageTypeSame: !!index,
        isCannedResponseVisible: true,
        isFeedbackMessage: this.isActionTypeFeedback(message.action.actionType),
        clickableLinks: clickableLinks,
        cannedResponses: cannedResponse,
      },
      actions: {
        onLinkClick: ActionType.LinkClick,
        onCannedResponseClick: ActionType.CannedResponseClick,
      },
    };
  }

  /**
   * Creates a Canned Response
   *
   * @param {string} messageTitle: the message title
   * @param {ApiSendChatMessageResponseActionItem[]} actionItems: the action items array
   */
  private createCannedResponse(
    messageTitle: string,
    actionItems: ApiSendChatMessageResponseActionItem[]
  ): CannedResponse[] {
    const length = actionItems.length;
    const lastIndexToBeMuted = this.getLastIndexToBeMuted(messageTitle, length);
    return actionItems.map(
      (actionItem: ApiSendChatMessageResponseActionItem, index) => {
        if (
          index <= lastIndexToBeMuted &&
          this.isCallbackTimeSelection(messageTitle)
        ) {
          return {
            value: actionItem.actionTitle,
            canBeMuted: true,
          };
        }
        return {
          value: actionItem.actionTitle,
          canBeMuted: false,
        };
      }
    );
  }
  private isCallbackTimeSelection(messageTitle: string): boolean {
    return (
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_SINGLE_DATE_MULTI_TIMESPAN ||
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_SINGLE_DATE_SINGLE_TIMESPAN ||
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_MULTI_DATE_SINGLE_TIMESPAN ||
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_MULTI_DATE_MULTI_TIMESPAN
    );
  }
  /**
   * Creates clickable links
   *
   * @param {ApiSendChatMessageResponseActionItem[]} actionItems: the action items from the API call
   */
  private createClickableLinks(
    actionItems: ApiSendChatMessageResponseActionItem[]
  ): ClickableLink[] {
    return actionItems.map(
      (actionItem: ApiSendChatMessageResponseActionItem) => {
        const isNewTab = !actionItem.actionTarget?.includes(
          RealmDomain[getBrand().name]
        );
        return {
          url: actionItem.actionTarget,
          title: actionItem.actionTitle,
          isNewTab,
          action: this.createClickableLinkAction(actionItem.actionTarget),
        };
      }
    );
  }
  /**
   * Adds Escalation Message to a message array
   *
   * @param {Message[]} messages - The array that will be appended
   * @param {ApiSendChatMessageResponseEscalation} escalation - The response that we build the escalation from
   */
  private addEscalationMessage(
    messages: Message[],
    escalation: ApiSendChatMessageResponseEscalation
  ) {
    if (escalation) {
      const escalationMessage: Message = {
        messageType: MessageType.VA,
        time: new Date(),
        escalation,
      };
      messages.push(escalationMessage);
    }
  }
  /**
   * Create a Clickable link depending on a string
   *
   * @param {string} actionTarget - The string that we may include urlPattern
   * @return {ClickableLinkAction} The created clickable link action
   */
  private createClickableLinkAction(
    actionTarget: string
  ): ClickableLinkAction | undefined {
    const clickableLinkAction = this.clickableLinkActionMapping.find(
      (mapping: UrlActionType) => actionTarget.startsWith(mapping.urlPattern)
    );
    const response = actionTarget.substring(actionTarget.indexOf('=') + 1);
    if (clickableLinkAction) {
      return {
        type: clickableLinkAction.actionType,
        response: response,
      };
    }
    return undefined;
  }

  /**
   * Get the last index to be muted
   *
   * @param {string} messageTitle - The message title that may include the callback
   * @param {number} length - The length of the action items
   */
  private getLastIndexToBeMuted(messageTitle: string, length: number): number {
    if (
      messageTitle ===
      ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_SINGLE_DATE_SINGLE_TIMESPAN
    ) {
      return length - 1;
    }
    if (
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_MULTI_DATE_SINGLE_TIMESPAN ||
      messageTitle ===
        ApiSendChatMessageResponseMessageTitleTypes.CALLBACK_SINGLE_DATE_MULTI_TIMESPAN
    ) {
      return length - 2;
    }
    return length - 3;
  }
}
